// plugins/AtomicBoxPlugin/components/EditableText.jsx
import React, { useRef, useEffect } from "react";

// Inject ::before placeholder CSS once per page load
let _phStyleInjected = false;
function ensurePlaceholderStyle() {
  if (_phStyleInjected) return;
  _phStyleInjected = true;
  if (typeof document === "undefined") return;
  if (document.getElementById("atomic-editable-ph-style")) return;
  const style = document.createElement("style");
  style.id = "atomic-editable-ph-style";
  style.textContent =
    "[data-editable-ph]:empty::before{content:attr(data-editable-ph);opacity:.35;}";
  document.head.appendChild(style);
}

/* eslint-disable react/prop-types */

/**
 * Inline-editable text span.
 *
 * @param {string}   props.value      Current text to display
 * @param {boolean}  props.editing    When true the span becomes editable
 * @param {function} props.onChange   Called with new string on blur / Enter
 * @param {object}   props.style      Optional extra inline styles
 * @param {string}   props.className  Optional class
 */
export function EditableText({ value, editing, onChange, style, className, placeholder }) {
  const ref = useRef(null);
  const escapedRef = useRef(false);

  useEffect(() => {
    if (editing && placeholder) ensurePlaceholderStyle();
  }, [editing, placeholder]);

  useEffect(() => {
    if (editing && ref.current) {
      ref.current.textContent = value ?? "";
      ref.current.focus();
      const range = document.createRange();
      range.selectNodeContents(ref.current);
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  }, [editing]);

  function handleBlur() {
    if (escapedRef.current) {
      escapedRef.current = false;
      return;
    }
    const newVal = ref.current?.textContent ?? "";
    if (newVal === (value ?? "")) return;
    if (onChange) onChange(newVal);
  }

  function handleKeyDown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      ref.current?.blur();
    }
    if (e.key === "Escape") {
      escapedRef.current = true;
      if (ref.current) ref.current.textContent = value ?? "";
      ref.current?.blur();
    }
    e.stopPropagation(); // prevent box-level shortcuts
  }

  return (
    <span
      ref={ref}
      contentEditable={editing || undefined}
      suppressContentEditableWarning
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={className}
      data-editable-ph={editing && placeholder ? placeholder : undefined}
      style={{
        outline: editing ? "2px solid var(--color-text-accent, #6c63ff)" : "none",
        outlineOffset: "2px",
        borderRadius: "3px",
        cursor: editing ? "text" : "inherit",
        userSelect: editing ? undefined : "none",
        minWidth: editing ? "4px" : undefined,
        display: editing && placeholder ? "inline-block" : undefined,
        width: editing && placeholder ? "100%" : undefined,
        ...style,
      }}
    >
      {editing ? undefined : value}
    </span>
  );
}

export default EditableText;
